import React, { useMemo, useState, useEffect } from "react";
import { View, Text, Image, TouchableOpacity, Linking, ActivityIndicator, ScrollView } from "react-native";
import lightStyles, { createHomeScreenThemeStyles } from "../HomeScreen.style";
import { TEXT } from "../../../constants/texts";
import { useSelector } from "react-redux";
import { FontFamily } from "../../../constants/fonts";
import SecurityIcon from './icons/SecurityIcon';
import { useTheme } from "../../../theme";

const trainingImage = require("../../../assets/training.png");

interface SafetyTopicItem {
  id: string;
  title: string;
  messageText: string;
  category: string;
  order: number;
  link: {
    Description: string;
    Url: string;
  };
  image: {
    Description: string;
    Url: string;
  };
  createdDate: string;
  modifiedDate: string;
}

interface TrainingEssentialItem {
  id: string | number;
  title: string;
  urlLinkForBanner?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}

interface SafetySpotlightProps {
  safetyTopics: SafetyTopicItem[] | TrainingEssentialItem[] | string[];
  setSafetyOverlayVisible: (isVisible: boolean) => void;
  bannerData?: any; // Add banner data prop
}

const SafetySpotlight: React.FC<SafetySpotlightProps> = ({
  safetyTopics,
  setSafetyOverlayVisible,
  bannerData,
}) => {
  const { colors } = useTheme();
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors), [colors]);
  // Get loading and error states from Redux
  const { loading, error } = useSelector((state: any) => state.homescreen);
  const isBannerLoading = loading.banner;
  const bannerError = error.banner;
  
  // Local state to handle image loading failures
  const [imageLoadError, setImageLoadError] = useState(false);
  
  // Helper function to handle URL opening with error handling
  const handleLinkPress = (url: string, description: string) => {
    if (url) {
      Linking.openURL(url).catch((err) => {
        console.error("An error occurred opening the link:", err);
      });
    } else {
      console.log(`No URL available for: ${description}`);
    }
  };
  // Memoize banner image URL to avoid multiple function calls
  const bannerImageUrl = useMemo(() => {
    if (bannerData && bannerData.data && bannerData.data.length > 0) {
      const firstBanner = bannerData.data[0];

      if (firstBanner && firstBanner.image && firstBanner.image.Url) {
        return firstBanner.image.Url;
      }
    }
    return null;
  }, [bannerData]);

  // Reset image load error when banner data changes
  useEffect(() => {
    setImageLoadError(false);
  }, [bannerImageUrl]);

  // Determine which image to show
  const getImageSource = () => {
    if (isBannerLoading) {
      return null; // Will show loading indicator
    }
    
    if (bannerError || !bannerImageUrl || imageLoadError) {
      return 'lightweight'; // Use lightweight component
    }
    
    return { uri: bannerImageUrl };
  };

  // Render the appropriate image component
  const renderImage = () => {
    const imageSource = getImageSource();
    
    if (imageSource === 'lightweight') {
      return (
        <Image
          source={trainingImage}
          style={lightStyles.safetyImage}
          resizeMode="contain"
        />
      );
    }
    
    if (imageSource && typeof imageSource === 'object') {
      return (
        <Image
          source={imageSource}
          style={lightStyles.safetyImage}
          resizeMode="contain"
          onError={() => {
            setImageLoadError(true);
          }}
          onLoad={() => {
            setImageLoadError(false);
          }}
        />
      );
    }
    
    return null;
  };

  // Function to handle banner click
  const handleBannerClick = () => {
    // Don't handle clicks during loading or if there's an error
    if (isBannerLoading || bannerError) {
      return;
    }
    
    if (bannerData && bannerData.data && bannerData.data.length > 0) {
      const firstBanner = bannerData.data[0];
      if (firstBanner && firstBanner.link && firstBanner.link.Url) {
        handleLinkPress(
          firstBanner.link.Url,
          firstBanner.link.Description || "Banner Link"
        );
      } else {
        console.log("Banner clicked but no link available");
      }
    }
  };

  // Function to check if item is SharePoint safety format
  const isNewFormat = (
    item: SafetyTopicItem | TrainingEssentialItem | string
  ): item is SafetyTopicItem => {
    return typeof item === "object" && item !== null && "link" in item;
  };

  // Function to check if item is training essential format
  const isTrainingEssential = (
    item: SafetyTopicItem | TrainingEssentialItem | string
  ): item is TrainingEssentialItem => {
    return typeof item === "object" && item !== null && "title" in item && !("link" in item);
  };

  const renderSafetyTopic = (
    topic: SafetyTopicItem | TrainingEssentialItem | string,
    _index: number
  ) => {
    if (isNewFormat(topic)) {
      // New API format with link support
      const hasLink = topic.link?.Url && topic.link?.Description;
      const displayText = hasLink ? topic.link.Description : topic.title;

      return (
        <View>
          <TouchableOpacity
            activeOpacity={0.8}
            style={lightStyles.linkRow}
            onPress={() => {
              if (hasLink) {
                handleLinkPress(topic.link.Url, topic.link.Description);
              } else {
                console.log(`Navigating to blank page for: ${topic.title}`);
              }
            }}
          >
            <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
              <SecurityIcon size={16} color="#005CBC" />
            </View>
            <View style={lightStyles.linkTextContainer}>
              <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]} numberOfLines={2}>
                {displayText}
              </Text>
            </View>
          </TouchableOpacity>
          <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
        </View>
      );
} else if (isTrainingEssential(topic)) {
      // Training essential format
      const url = topic.urlLinkForBanner || "";
      const hasLink = Boolean(url);
      return (
        <View>
          <TouchableOpacity
            activeOpacity={0.8}
            style={lightStyles.linkRow}
            onPress={() => {
              if (hasLink) {
                handleLinkPress(url, topic.title);
              } else {
                console.log(`Navigating to blank page for: ${topic.title}`);
              }
            }}
          >
            <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
              <SecurityIcon size={16} color="#005CBC" />
            </View>
            <View style={lightStyles.linkTextContainer}>
              <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]} numberOfLines={2}>
                {topic.title}
              </Text>
            </View>
          </TouchableOpacity>
          <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
        </View>
      );
    } else {
      // Legacy string format
      return (
        <View>
          <TouchableOpacity
            activeOpacity={0.8}
            style={lightStyles.linkRow}
            onPress={() => console.log(`Navigating to blank page for: ${topic}`)}
          >
            <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
              <SecurityIcon size={16} color="#005CBC" />
            </View>
            <View style={lightStyles.linkTextContainer}>
              <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]} numberOfLines={2}>
                {topic as string}
              </Text>
            </View>
          </TouchableOpacity>
          <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
        </View>
      );
    }
  };

  // Check if we have data to display
  const hasData = safetyTopics && safetyTopics.length > 0;
  return (
    <View style={[lightStyles.bottomCard, themeStyles.cardSurface]}>
      <Text style={[lightStyles.sectionHeader, themeStyles.sectionHeader]}>{TEXT.HEADERS.SAFETY_SPOTLIGHT}</Text>
      <View style={lightStyles.safetyImageContainer} testID="safety-topic">
        <TouchableOpacity onPress={handleBannerClick}>
          {isBannerLoading ? (
            <View style={[lightStyles.safetyImage, { 
              justifyContent: 'center', 
              alignItems: 'center',
              backgroundColor: colors.background.elevated,
            }]}>
              <ActivityIndicator size="large" color={colors.brand.primary} />
              <Text style={[lightStyles.externalLoadingText, themeStyles.externalLoadingText]}>
                Loading banner...
              </Text>
            </View>
          ) : (
            renderImage()
          )}
        </TouchableOpacity>
      </View>
      <View style={lightStyles.safetyTopics}>
        {loading.trainingEssentials ? (
          <View style={{
            justifyContent: 'center',
            alignItems: 'center',
            paddingVertical: 20
          }}>
            <ActivityIndicator size="small" color={colors.brand.primary} />
            <Text style={[lightStyles.safetyLoadingText, themeStyles.safetyLoadingText]}>
              Loading safety topics...
            </Text>
          </View>
        ) : hasData ? (
          <ScrollView style={lightStyles.scrollContent}>
            {safetyTopics.map((topic: any, _index: number) => (
              <React.Fragment key={typeof topic === 'object' ? (topic.id || _index) : `topic-${_index}`}>
                {renderSafetyTopic(topic, _index)}
              </React.Fragment>
            ))}
          </ScrollView>
        ) : (
          <View style={lightStyles.noDataContainer}>
            <Text style={[lightStyles.noDataText, themeStyles.noDataText]}>{TEXT.COMMON.NO_DATA}</Text>
          </View>
        )}
      </View>
    </View>
  );
};

export default SafetySpotlight;
